import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { LangLink as Link } from "@/components/LangLink";
import ReactMarkdown from "react-markdown";
import SEO from "@/components/SEO";
import { api } from "@/lib/api";
import { formatDateLongSQ } from "@/lib/date";
import FinalCTA from "@/components/FinalCTA";
import { ChevronRight } from "lucide-react";
import { useT } from "@/hooks/useT";
import { useI18n } from "@/contexts/I18nContext";

export default function BlogPost() {
  const { slug } = useParams();
  const t = useT();
  const { lang } = useI18n();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setPost(null);
    setNotFound(false);
    api.get(`/blog/posts/${slug}`).then((r) => setPost(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <section className="container-akm py-20">
        <h1 className="h1-akm">{t("Postimi nuk u gjet", "Post not found")}</h1>
        <p className="lead mt-3">{t("Postimi që po kërkoni nuk ekziston ose është hequr.", "The post you're looking for doesn't exist or has been removed.")}</p>
        <Link to="/blog" className="btn-primary mt-6">{t("Kthehu te Blog", "Back to Blog")}</Link>
      </section>
    );
  }
  if (!post) {
    return <div className="container-akm py-20 text-sm text-slate2">{t("Duke ngarkuar…", "Loading…")}</div>;
  }

  // Localised copy — falls back to Albanian when EN version is missing.
  const isEn = lang === "en";
  const title    = (isEn && post.title_en)   || post.title;
  const excerpt  = (isEn && post.excerpt_en) || post.excerpt;
  const content  = (isEn && post.content_en) || post.content;
  const metaT    = (isEn && (post.meta_title_en   || post.title_en))   || post.meta_title || post.title;
  const metaD    = (isEn && (post.meta_description_en || post.excerpt_en)) || post.meta_description || post.excerpt || "";
  // Social share image — always the dynamic OG (branded, 1200×630 with title).
  // The Albanian in-page hero image still uses post.cover_image below.
  const apiRoot = process.env.REACT_APP_BACKEND_URL || "";
  const ogImage = `${apiRoot}/api/og/blog/${encodeURIComponent(post.slug)}.png?lang=${isEn ? "en" : "sq"}`;

  const schema = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: title,
    datePublished: post.created_at,
    dateModified: post.updated_at,
    image: post.cover_image ? [post.cover_image] : undefined,
    description: metaD,
    inLanguage: isEn ? "en" : "sq",
    author: { "@type": "Organization", name: "Akomoda" },
    publisher: { "@type": "Organization", name: "Akomoda" },
  };

  return (
    <>
      <SEO
        title={metaT}
        description={metaD}
        ogImage={ogImage}
        schema={schema}
      />

      <section className="bg-surface-1">
        <div className="container-akm py-12 md:py-16">
          <nav className="mb-5 flex items-center gap-1 text-xs text-slate2">
            <Link to="/" className="hover:text-[#FF7A00]">{t("Kreu", "Home")}</Link>
            <ChevronRight size={12} />
            <Link to="/blog" className="hover:text-[#FF7A00]">Blog</Link>
            <ChevronRight size={12} />
            <span className="text-ink">{title}</span>
          </nav>

          {post.tags?.length > 0 && (
            <div className="mb-3 flex flex-wrap gap-2">
              {post.tags.map((tag) => (
                <span key={tag} className="rounded-full border border-line bg-bg px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider text-slate2">{tag}</span>
              ))}
            </div>
          )}
          <h1 className="h1-akm max-w-3xl" data-testid="blog-post-title">{title}</h1>
          <div className="mt-3 text-sm text-slate2">{formatDateLongSQ(post.created_at)}</div>

          {post.cover_image && (
            <div className="mt-8 overflow-hidden rounded-xl border border-line">
              <img src={post.cover_image} alt={title} className="w-full" loading="lazy" decoding="async" />
            </div>
          )}
        </div>
      </section>

      <article className="bg-surface-1">
        <div className="container-akm grid gap-10 py-12 md:grid-cols-12 md:py-16">
          <div className="prose prose-slate max-w-none md:col-span-8 text-[16px] leading-[1.75] text-ink" data-testid="blog-post-content">
            <ReactMarkdown
              components={{
                h1: ({ node, ...p }) => <h1 className="h1-akm mt-6 mb-3" {...p} />,
                h2: ({ node, ...p }) => <h2 className="h2-akm mt-8 mb-3" {...p} />,
                h3: ({ node, ...p }) => <h3 className="h3-akm mt-6 mb-2" {...p} />,
                p: ({ node, ...p }) => <p className="mb-4 text-[16px] leading-[1.75] text-slate2" {...p} />,
                a: ({ node, ...p }) => <a className="link-akm" {...p} />,
                ul: ({ node, ...p }) => <ul className="mb-4 list-disc pl-5 text-slate2" {...p} />,
                ol: ({ node, ...p }) => <ol className="mb-4 list-decimal pl-5 text-slate2" {...p} />,
                li: ({ node, ...p }) => <li className="mb-1.5" {...p} />,
                blockquote: ({ node, ...p }) => <blockquote className="my-4 border-l-4 border-[#FF7A00] bg-bg p-4 italic text-slate2" {...p} />,
                code: ({ node, ...p }) => <code className="rounded bg-bg px-1.5 py-0.5 text-sm text-ink" {...p} />,
                img: ({ node, ...p }) => <img className="my-4 rounded-lg border border-line" {...p} alt={p.alt || ""} />,
              }}
            >
              {content}
            </ReactMarkdown>
          </div>

          <aside className="md:col-span-4">
            <div className="sticky top-24 space-y-4">
              <div className="card-akm">
                <div className="font-display text-base font-bold text-ink">{t("Kërko Demo", "Request Demo")}</div>
                <p className="mt-1 text-sm text-slate2">{t("Shihni Akomoda në veprim me një demo personale.", "See Akomoda in action with a personal demo.")}</p>
                <Link to="/demo" className="btn-primary mt-3 w-full justify-center">{t("Kërko Demo", "Request Demo")}</Link>
              </div>
              <div className="card-akm">
                <div className="text-xs font-semibold uppercase tracking-wider text-slate2">{t("Eksploro", "Explore")}</div>
                <ul className="mt-2 space-y-2 text-sm">
                  <li><Link to="/pms" className="link-akm">{t("Program për Hotele (PMS)", "Hotel PMS")}</Link></li>
                  <li><Link to="/channel-manager" className="link-akm">Channel Manager</Link></li>
                  <li><Link to="/booking-engine" className="link-akm">Booking Engine</Link></li>
                  <li><Link to="/cmimet" className="link-akm">{t("Çmimet", "Pricing")}</Link></li>
                </ul>
              </div>
            </div>
          </aside>
        </div>
      </article>

      <FinalCTA />
    </>
  );
}
